import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { LayoutDashboard, Users, Ticket, X } from 'lucide-react'
import { cn } from '../lib/utils'
import { queryClient } from '../lib/queryClient'
import { useAccess } from '../hooks/useAccess'
import { prefetchVolumeTrends } from '../services/api/trendService'
import { ensureAgentTrends } from '../services/api/agentService'

// Default Ticket and Trend Requests
function last7DaysTickets() {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - 6)
  const fmt = (d: Date) => d.toISOString().slice(0, 10)
  return { from_date: fmt(start), to_date: fmt(end) }
}

const DEFAULT_TICKETS_REQ = {
  ...last7DaysTickets(),
  agent_name: '',
  phone_number: '',
  ticket_id: 0,
  model_number: '',
  client: '',
  type: '',
  status: '',
  product_category: '',
  error_code: '',
  resolution: '',
  page_number: 1,
  page_size: 25
}

function lastNDaysTrends(n: number) {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - (n - 1))
  const fmt = (d: Date) => d.toISOString().slice(0, 10)
  return { start_date: fmt(start), end_date: fmt(end) }
}

const DEFAULT_TRENDS_REQ = {
  ...lastNDaysTrends(30),
  verbose: false,
  echo_result_columns: true
}

// Prefetch dashboard-related data
function prefetchDashboard() {
  prefetchVolumeTrends(queryClient, DEFAULT_TRENDS_REQ)
  ensureAgentTrends(queryClient, { ...DEFAULT_TRENDS_REQ, contact_name: '' })
}

interface SidebarProps {
  isOpen: boolean
  onClose: () => void
}

const Sidebar: React.FC<SidebarProps> = ({ isOpen, onClose }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { can } = useAccess()

  const menuItems = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/',
      description: 'Real-time metrics and analytics',
      prefetch: () => prefetchDashboard(),
      feature: 'dashboard' as const
    },
    {
      id: 'tickets',
      label: 'Tickets',
      icon: Ticket,
      path: '/tickets',
      description: 'Ticket management and tracking',
      feature: 'tickets' as const
    },
    {
      id: 'leaderboard',
      label: 'Leaderboard',
      icon: Users,
      path: '/leaderboard',
      description: 'Agent leaderboard',
      feature: 'leaderboard' as const
    }
  ]

  const visibleItems = menuItems.filter(item => 
    item.feature ? can(item.feature) : true
  )

  const handleNavigation = (path: string) => {
    navigate(path)
    onClose()
  }

  const handlePrefetch = (prefetch?: () => void) => {
    try {
      prefetch?.()
    } catch {
      // Ignore prefetch errors; navigation should still work
    }
  }

  return (
    <>
      {isOpen && (
        <div
          className='fixed inset-0 z-50 bg-black/50 backdrop-blur-sm'
          onClick={onClose}
        />
      )}

      <div
        className={cn(
          'fixed left-0 top-0 z-50 h-full w-80 transform bg-white shadow-2xl transition-transform duration-300 ease-in-out',
          isOpen ? 'translate-x-0' : '-translate-x-full'
        )}
        role='dialog'
        aria-modal='true'
        aria-label='Navigation'
      >
        <div className='flex items-center justify-between border-b border-gray-200 p-6'>
          <button
            onClick={() => prefetchDashboard()}
            onMouseEnter={prefetchDashboard}
            className='group flex cursor-pointer select-none items-center gap-1'
            title='Go to Dashboard'
          >
            <img
              src='/images/proserve-logo.png'
              alt='ProServe'
              className='h-4 w-auto object-contain'
              draggable={false}
            />
            <span className='text-xs font-bold text-gray-900 transition-colors group-hover:text-blue-600'>
              CS Portal
            </span>
          </button>

          <button
            onClick={onClose}
            className='rounded-lg p-2 transition-colors duration-200 hover:bg-gray-100'
            aria-label='Close navigation menu'
          >
            <X className='h-5 w-5 text-gray-500' />
          </button>
        </div>

        <nav className='p-6'>
          <div className='space-y-2'>
            {visibleItems.length === 0 ? (
              <div className='text-center text-sm text-gray-400'>
                No menu items available for this organization
              </div>
            ) : (
              visibleItems.map(item => {
                const Icon = item.icon
                const isActive = location.pathname === item.path

                return (
                  <button
                    key={item.id}
                    onClick={() => handleNavigation(item.path)}
                    onMouseEnter={() => handlePrefetch(item.prefetch)}
                    onFocus={() => handlePrefetch(item.prefetch)}
                    className={cn(
                      'flex w-full items-center gap-4 rounded-xl p-4 text-left transition-all duration-200',
                      isActive
                        ? 'border border-slate-200 bg-slate-100 shadow-sm'
                        : 'border border-transparent hover:bg-gray-50'
                    )}
                    aria-current={isActive ? 'page' : undefined}
                  >
                    <div
                      className={cn(
                        'flex h-10 w-10 items-center justify-center rounded-lg',
                        isActive
                          ? 'bg-slate-600 text-white'
                          : 'bg-gray-100 text-gray-600'
                      )}
                    >
                      <Icon className='h-5 w-5' />
                    </div>
                    <div className='flex-1'>
                      <div
                        className={cn(
                          'font-medium',
                          isActive ? 'text-slate-900' : 'text-gray-900'
                        )}
                      >
                        {item.label}
                      </div>
                      <div className='mt-1 text-xs text-gray-500'>
                        {item.description}
                      </div>
                    </div>
                    {isActive && (
                      <div className='h-2 w-2 rounded-full bg-slate-600' />
                    )}
                  </button>
                )
              })
            )}
          </div>
        </nav>

        <div className='absolute bottom-0 left-0 right-0 border-t border-gray-200 bg-gray-50 p-6'>
          <div className='text-center'>
            <a
              href='https://proservecs.com/'
              target='_blank'
              rel='noopener noreferrer'
              className='text-slate-600 underline hover:text-slate-800'
            >
              Pro-Serve CS
            </a>
            <div className='mt-1 text-xs text-gray-400'>Version 1.0</div>
          </div>
        </div>
      </div>
    </>
  )
}

export default Sidebar
